import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getAdminsForEvent } from '@modules/supabase/events';
import { EventRoleStatus } from '@type/db-types';
import MaterialTable, {
    materialTableHeight,
    TableActions,
} from '@components/molecules/MaterialTable';
import { Column, Options, SvgIconComponent } from '@material-table/core';
import { CopyToClipboardColumn } from '@atoms/CopyToClipboardColumn';
import RefreshIcon from '@mui/icons-material/Refresh';
import useEventContext from '@hooks/useEventContext';
import { Divider } from 'antd';
import EventRoleSelector from '@atoms/EventRoleSelector';
import ManagePaymentAdmin from '@atoms/ManagePaymentAdmin';
import RenderStatusTag from '@atoms/RenderStatusTag';
import { STATUS_LOOKUP } from '@utils/team-table-utils';
import { EVENT_ROLE_LOOKUP } from '@utils/event-utils';
import FAB from '@atoms/FAB';
import AddAdminModal from '@components/molecules/AddAdminModal';

export default function EventPermissionsTable() {
    const { event } = useEventContext();
    const [admins, setAdmins] = useState<EventRoleStatus[] | null>(null);
    const tableRef: React.MutableRefObject<undefined | unknown> = useRef();
    const [showAddEventAdmin, setShowAddEventAdmin] = useState(false);

    const refreshAdmins = useCallback(
        () => event?.id && getAdminsForEvent(event.id).then(setAdmins),
        [event?.id],
    );

    useEffect(() => {
        refreshAdmins();
    }, [event?.id, refreshAdmins]);

    const columns: Column<EventRoleStatus>[] = useMemo(
        () => getTableColumns(refreshAdmins),
        [refreshAdmins],
    );

    const actions: TableActions<EventRoleStatus> = useMemo(
        () => [
            {
                icon: RefreshIcon as SvgIconComponent,
                tooltip: 'Refresh Data',
                isFreeAction: true,
                onClick: refreshAdmins,
            },
        ],

        [refreshAdmins],
    );

    return (
        <div>
            <ManagePaymentAdmin />
            <Divider />
            <MaterialTable<EventRoleStatus>
                style={{ boxShadow: 'none' }}
                tableRef={tableRef}
                actions={actions}
                columns={columns}
                data={admins || []}
                localization={{
                    toolbar: {
                        nRowsSelected: (number) =>
                            `${number} admins${number === 1 ? '' : 's'} selected`,
                    },
                }}
                loading={admins === null}
                options={TABLE_OPTIONS}
                tableId="Event-Admins-Table"
                title="Event Admins"
            />
            <FAB onClick={() => setShowAddEventAdmin(true)} toolTip="Add event admin" />
            <AddAdminModal
                open={showAddEventAdmin}
                onClose={() => {
                    setShowAddEventAdmin(false);
                    refreshAdmins();
                }}
            />
        </div>
    );
}

const getTableColumns: (refreshUsers: () => void) => Column<EventRoleStatus>[] = (refreshUsers) => [
    { title: 'First Name', field: 'firstName' },
    { title: 'Last Name', field: 'lastName' },
    {
        lookup: EVENT_ROLE_LOOKUP,
        title: 'Role',
        field: 'membershipRole',
        hidden: false,
        render: (user) => <EventRoleSelector user={user} refresh={refreshUsers} />,
    },
    {
        title: 'Email',
        field: 'email',
        render: (data) => <CopyToClipboardColumn text={data.email || ''} />,
    },
    {
        lookup: STATUS_LOOKUP,
        field: 'membershipStatus',
        render: RenderStatusTag,
        title: 'Status',
    },
    {
        field: 'userId',
        filtering: false,
        hidden: true,
        title: 'ID',
        render: (data) => <CopyToClipboardColumn text={data.userId || ''} />,
    },
    {
        title: 'Phone',
        field: 'phone',
        hidden: true,
        render: (data) => (data.phone ? <CopyToClipboardColumn text={data.phone} /> : '...'),
    },
];

const TABLE_OPTIONS: Options<EventRoleStatus> = {
    actionsColumnIndex: -1,
    maxBodyHeight: `40vh`,
    minBodyHeight: `${materialTableHeight - 21}vh`,
};
